import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";
import { showSnackbar } from "./snackbar";

const baseUrl = getBaseUrl();

const WIDGET_ORDER_API = `${baseUrl}/widget-layout`;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "use-case": "src",
  ...getAuthApiHeaders(),
  client: "UT",
});

/** Redux key: default dashboard is `roleKey` only; scoped layouts use `roleKey::scope`. */
export function widgetOrderStateKey(roleKey, layoutScope = null) {
  const s = layoutScope?.trim();
  if (!s) return roleKey;
  return `${roleKey}::${s}`;
}

function parseGetWidgetOrderArg(arg) {
  if (typeof arg === "string") {
    return { roleKey: arg, layoutScope: null };
  }
  const roleKey = arg?.roleKey;
  const layoutScope = arg?.layoutScope?.trim() || null;
  return { roleKey, layoutScope: layoutScope || null };
}

export const getWidgetOrder = createAsyncThunk(
  "widgetOrder/getWidgetOrder",
  async (arg, { rejectWithValue, dispatch }) => {
    const { roleKey, layoutScope } = parseGetWidgetOrderArg(arg);
    try {
      const params = { role_key: roleKey };
      if (layoutScope) params.layout_scope = layoutScope;

      const response = await axios.get(WIDGET_ORDER_API, {
        headers: getHeaders(),
        params,
        withCredentials: true,
      });

      return {
        roleKey,
        layoutScope,
        data: response.data,
      };
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return {
          roleKey,
          layoutScope,
          data: null,
        };
      }

      dispatch(
        showSnackbar({
          message: "Failed to load widget order!",
          severity: "error",
        }),
      );

      return rejectWithValue(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.statusText ||
          error.message ||
          "Failed to load widget order",
      );
    }
  },
);

export const saveWidgetOrder = createAsyncThunk(
  "widgetOrder/saveWidgetOrder",
  async (
    { roleKey, layout, layoutScope = null },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const tiles = layout.map((item, index) => ({
        id: item.id,
        type: item.type,
        order: index + 1,
      }));
      const payload = {
        role_key: roleKey,
        tiles,
        ...(layoutScope ? { layout_scope: layoutScope } : {}),
      };

      const response = await axios.put(WIDGET_ORDER_API, payload, {
        headers: getHeaders(),
        withCredentials: true,
      });

      dispatch(
        showSnackbar({
          message: "Widget order saved successfully!",
          severity: "success",
        }),
      );

      return {
        roleKey,
        layoutScope: layoutScope || null,
        data: response.data,
      };
    } catch (error) {
      dispatch(
        showSnackbar({
          message: "Failed to save widget order!",
          severity: "error",
        }),
      );

      return rejectWithValue(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.statusText ||
          error.message ||
          "Failed to save widget order",
      );
    }
  },
);

const widgetOrderSlice = createSlice({
  name: "widget-order",
  initialState: {
    isLoading: false,
    isSaving: false,
    widgetOrderByRole: {},
    isError: false,
    error: null,
    success: false,
  },
  reducers: {
    setWidgetOrderLocal: (state, action) => {
      const { roleKey, layout, layoutScope = null } = action.payload;
      const key = widgetOrderStateKey(roleKey, layoutScope);
      state.widgetOrderByRole[key] = layout;
    },
    clearWidgetOrderState: (state) => {
      state.isError = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWidgetOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.isError = false;
        state.success = false;
      })
      .addCase(getWidgetOrder.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;

        const { roleKey, layoutScope, data } = action.payload;
        const key = widgetOrderStateKey(roleKey, layoutScope);

        state.widgetOrderByRole[key] = data?.tiles || [];
      })
      .addCase(getWidgetOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
        state.success = false;
      })

      .addCase(saveWidgetOrder.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        state.isError = false;
      })
      .addCase(saveWidgetOrder.fulfilled, (state, action) => {
        state.isSaving = false;

        const { roleKey, layoutScope, data } = action.payload;
        const key = widgetOrderStateKey(roleKey, layoutScope);
        if (data?.tiles) {
          state.widgetOrderByRole[key] = data.tiles;
        }
      })
      .addCase(saveWidgetOrder.rejected, (state, action) => {
        state.isSaving = false;
        state.isError = true;
        state.error = action.payload;
      });
  },
});

export const { setWidgetOrderLocal, clearWidgetOrderState } =
  widgetOrderSlice.actions;

export default widgetOrderSlice.reducer;
